import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Save, ExternalLink, Plus, Trash2, Image, Youtube, ChevronUp, ChevronDown } from "lucide-react";
import SiteImageField from "@/components/SiteImageField";
import {
  HOME_EDITOR_SECTIONS,
  PUBLIC_SITE_FIELD_META,
  STAT_ICON_OPTIONS,
  mergePublicSiteForEditor,
  publicSiteToPayload,
  dispatchPublicSiteUpdated,
} from "@/lib/gymSiteDefaults";
import { parseYoutubeVideoId } from "@/lib/youtube";
import {
  BrandFooterPreview,
  HeroPreview,
  StatsPreview,
  PackagesSectionPreview,
  EventsSectionPreview,
  CtaPreview,
  NavPreview,
  PageHeaderPreview,
} from "@/components/sitePublicPreviews";

function SectionCard({ id, title, description, children, preview }) {
  return (
    <div id={id} className="glass rounded-sm scroll-mt-24">
      <div className="p-5 border-b border-theme-secondary/10">
        <h2 className="font-display text-2xl text-white">{title}</h2>
        {description && <p className="text-sm text-white/40 mt-1">{description}</p>}
      </div>
      <div className="p-5 grid gap-8 xl:grid-cols-2 xl:items-start">
        <div className="space-y-4">{children}</div>
        <div className="xl:sticky xl:top-4">{preview}</div>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-white/60">{label}</label>
      {hint && <p className="text-[10px] text-white/40 mt-0.5 mb-1">{hint}</p>}
      <div className="mt-1">{children}</div>
    </div>
  );
}

function TextInput({ value, onChange, multiline, rows = 3 }) {
  const cls = "input-dark w-full" + (multiline ? " resize-none" : "");
  if (multiline) {
    return <textarea rows={rows} className={cls} value={value || ""} onChange={(e) => onChange(e.target.value)} />;
  }
  return <input className={cls} value={value || ""} onChange={(e) => onChange(e.target.value)} />;
}

function metaLabel(key, fallback) {
  return PUBLIC_SITE_FIELD_META[key]?.label || fallback;
}

function metaHint(key) {
  return PUBLIC_SITE_FIELD_META[key]?.hint;
}

export default function AdminPublicSite() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [ytUrl, setYtUrl] = useState("");

  useEffect(() => {
    api.get("/admin/public-site").then((r) => setForm(mergePublicSiteForEditor(r.data))).catch(() => {
      toast.error("No se pudo cargar el sitio público");
    });
  }, []);

  if (!form) return <div className="text-white/40">Cargando…</div>;

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const setPage = (pageKey, field, value) =>
    setForm((f) => ({ ...f, [pageKey]: { ...(f[pageKey] || {}), [field]: value } }));

  const slides = form.hero_carousel_slides || [];

  const addImageSlide = async (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      set("hero_carousel_slides", [...slides, { type: "image", src: reader.result }]);
      toast.success("Imagen agregada al carrusel");
    };
    reader.readAsDataURL(file);
  };

  const addYoutubeSlide = () => {
    const url = ytUrl.trim();
    if (!url) return toast.error("Pega la URL del video");
    if (!parseYoutubeVideoId(url)) return toast.error("URL de YouTube no válida");
    set("hero_carousel_slides", [...slides, { type: "youtube", src: url }]);
    setYtUrl("");
    toast.success("Video agregado");
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/public-site", publicSiteToPayload(form));
      const merged = mergePublicSiteForEditor(data);
      setForm(merged);
      dispatchPublicSiteUpdated(merged);
      toast.success("Sitio público guardado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-public-site-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[#E63946]">Marketing</div>
          <h1 className="font-display text-4xl text-white mt-1">Sitio público</h1>
          <p className="text-sm text-white/40 mt-2 max-w-xl">
            Contenido compartido del home y páginas principales. Los cambios se publican al guardar.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <a href="/" target="_blank" rel="noreferrer" className="btn-outline text-sm py-2">
            <ExternalLink size={14} /> Ver sitio
          </a>
          <button type="button" onClick={save} disabled={saving} className="btn-primary text-sm py-2">
            <Save size={14} /> {saving ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </div>

      <nav className="mt-6 flex flex-wrap gap-2">
        {HOME_EDITOR_SECTIONS.map((sec) => (
          <a
            key={sec.id}
            href={`#${sec.id}`}
            className="text-xs px-3 py-1.5 rounded-sm border border-white/15 text-white/60 hover:text-white hover:border-white/30"
          >
            {sec.title}
          </a>
        ))}
      </nav>

      <div className="mt-8 space-y-8">
        <SectionCard
          id="edit-brand"
          title="Marca y contacto"
          description={HOME_EDITOR_SECTIONS[0].description}
          preview={<BrandFooterPreview site={form} />}
        >
          <SiteImageField label="Logotipo" value={form.logo} onChange={(v) => set("logo", v)} previewVariant="logo" />
          <Field label={metaLabel("gym_name", "Nombre")} hint={metaHint("gym_name")}>
            <TextInput value={form.gym_name} onChange={(v) => set("gym_name", v)} />
          </Field>
          <Field label={metaLabel("address", "Dirección")} hint={metaHint("address")}>
            <TextInput value={form.address} onChange={(v) => set("address", v)} />
          </Field>
          <Field label={metaLabel("phone", "Teléfono")} hint={metaHint("phone")}>
            <TextInput value={form.phone} onChange={(v) => set("phone", v)} />
          </Field>
          <Field label={metaLabel("contact_email", "Email")} hint={metaHint("contact_email")}>
            <TextInput value={form.contact_email} onChange={(v) => set("contact_email", v)} />
          </Field>
          <Field label={metaLabel("footer_blurb", "Texto del pie")} hint={metaHint("footer_blurb")}>
            <TextInput value={form.footer_blurb} onChange={(v) => set("footer_blurb", v)} multiline rows={2} />
          </Field>
          <div className="rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-sm text-zinc-400">
            <p className="font-medium text-zinc-300">Copyright de la plataforma</p>
            <p className="mt-1">
              © {new Date().getFullYear()} DanySolutions. Todos los derechos reservados.
            </p>
            <p className="mt-2 text-xs text-zinc-500">
              Firma fija del servicio por suscripción; no es editable.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {["facebook", "instagram", "twitter", "youtube"].map((k) => (
              <Field key={k} label={k.charAt(0).toUpperCase() + k.slice(1)}>
                <TextInput value={form[k]} onChange={(v) => set(k, v)} />
              </Field>
            ))}
          </div>
        </SectionCard>

        <SectionCard
          id="edit-hero"
          title="Hero (inicio)"
          description={HOME_EDITOR_SECTIONS[1].description}
          preview={<HeroPreview site={form} />}
        >
          <Field label={metaLabel("hero_badge", "Etiqueta")} hint={metaHint("hero_badge")}>
            <TextInput value={form.hero_badge} onChange={(v) => set("hero_badge", v)} />
          </Field>
          <div className="grid sm:grid-cols-3 gap-3">
            <Field label={metaLabel("hero_title_prefix", "Título 1")} hint={metaHint("hero_title_prefix")}>
              <TextInput value={form.hero_title_prefix} onChange={(v) => set("hero_title_prefix", v)} />
            </Field>
            <Field label={metaLabel("hero_title_highlight", "Destacado")} hint={metaHint("hero_title_highlight")}>
              <TextInput value={form.hero_title_highlight} onChange={(v) => set("hero_title_highlight", v)} />
            </Field>
            <Field label={metaLabel("hero_title_suffix", "Título 2")} hint={metaHint("hero_title_suffix")}>
              <TextInput value={form.hero_title_suffix} onChange={(v) => set("hero_title_suffix", v)} />
            </Field>
          </div>
          <Field label={metaLabel("hero_subtitle", "Subtítulo")} hint={metaHint("hero_subtitle")}>
            <TextInput value={form.hero_subtitle} onChange={(v) => set("hero_subtitle", v)} multiline rows={3} />
          </Field>
          <div className="grid sm:grid-cols-3 gap-3">
            <Field label="Botón principal">
              <TextInput value={form.hero_btn_primary} onChange={(v) => set("hero_btn_primary", v)} />
            </Field>
            <Field label="Botón secundario">
              <TextInput value={form.hero_btn_secondary} onChange={(v) => set("hero_btn_secondary", v)} />
            </Field>
            <Field label="Botón contacto">
              <TextInput value={form.hero_btn_contact} onChange={(v) => set("hero_btn_contact", v)} />
            </Field>
          </div>
          <Field label="Segundos por diapositiva del carrusel">
            <input
              type="number"
              min={3}
              max={60}
              className="input-dark w-full max-w-xs"
              value={form.hero_carousel_interval_sec ?? 6}
              onChange={(e) => set("hero_carousel_interval_sec", parseInt(e.target.value, 10) || 6)}
            />
          </Field>
          <div className="space-y-3 border-t border-white/10 pt-4">
            <p className="text-xs text-white/60">Carrusel de fondo (imágenes o YouTube)</p>
            {slides.map((sl, idx) => (
              <div key={idx} className="flex items-center gap-2 glass p-2 rounded-sm">
                {sl.type === "youtube" ? <Youtube size={16} className="text-red-400 shrink-0" /> : <Image size={16} className="text-white/60 shrink-0" />}
                <span className="text-xs text-white/60 truncate flex-1">{sl.type === "youtube" ? sl.src : "Imagen"}</span>
                <button type="button" onClick={() => set("hero_carousel_slides", slides.filter((_, i) => i !== idx))} className="text-red-400 p-1"><Trash2 size={14} /></button>
                <button type="button" disabled={idx === 0} onClick={() => { const n = [...slides]; [n[idx - 1], n[idx]] = [n[idx], n[idx - 1]]; set("hero_carousel_slides", n); }} className="p-1 text-white/40"><ChevronUp size={14} /></button>
                <button type="button" disabled={idx === slides.length - 1} onClick={() => { const n = [...slides]; [n[idx], n[idx + 1]] = [n[idx + 1], n[idx]]; set("hero_carousel_slides", n); }} className="p-1 text-white/40"><ChevronDown size={14} /></button>
              </div>
            ))}
            <div className="flex flex-wrap gap-2">
              <label className="btn-outline text-xs py-2 cursor-pointer">
                <Plus size={12} /> Imagen
                <input type="file" accept="image/*" className="hidden" onChange={(e) => addImageSlide(e.target.files?.[0])} />
              </label>
              <input className="input-dark text-xs flex-1 min-w-[180px]" placeholder="URL YouTube" value={ytUrl} onChange={(e) => setYtUrl(e.target.value)} />
              <button type="button" onClick={addYoutubeSlide} className="btn-outline text-xs py-2"><Youtube size={12} /> Agregar video</button>
            </div>
          </div>
        </SectionCard>

        <SectionCard id="edit-stats" title="Estadísticas" description={HOME_EDITOR_SECTIONS[2].description} preview={<StatsPreview site={form} />}>
          {(form.home_stats || []).map((st, idx) => (
            <div key={idx} className="glass p-3 rounded-sm space-y-2">
              <div className="text-xs text-white/40">Tarjeta {idx + 1}</div>
              <div className="grid sm:grid-cols-3 gap-2">
                <Field label="Icono">
                  <select className="input-dark" value={st.icon || "trophy"} onChange={(e) => {
                    const stats = [...form.home_stats];
                    stats[idx] = { ...stats[idx], icon: e.target.value };
                    set("home_stats", stats);
                  }}>
                    {STAT_ICON_OPTIONS.map((o) => (
                      <option key={o.id} value={o.id}>{o.label}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Valor">
                  <TextInput value={st.value} onChange={(v) => {
                    const stats = [...form.home_stats];
                    stats[idx] = { ...stats[idx], value: v };
                    set("home_stats", stats);
                  }} />
                </Field>
                <Field label="Etiqueta">
                  <TextInput value={st.label} onChange={(v) => {
                    const stats = [...form.home_stats];
                    stats[idx] = { ...stats[idx], label: v };
                    set("home_stats", stats);
                  }} />
                </Field>
              </div>
            </div>
          ))}
        </SectionCard>

        <SectionCard id="edit-packages" title="Paquetes (home)" description={HOME_EDITOR_SECTIONS[3].description} preview={<PackagesSectionPreview site={form} />}>
          <Field label="Etiqueta superior"><TextInput value={form.packages_eyebrow} onChange={(v) => set("packages_eyebrow", v)} /></Field>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Título — parte 1"><TextInput value={form.packages_title_prefix} onChange={(v) => set("packages_title_prefix", v)} /></Field>
            <Field label="Título — destacado"><TextInput value={form.packages_title_highlight} onChange={(v) => set("packages_title_highlight", v)} /></Field>
          </div>
          <Field label="Enlace «ver todos»"><TextInput value={form.packages_link_label} onChange={(v) => set("packages_link_label", v)} /></Field>
          <Field label="Badge «más popular»"><TextInput value={form.popular_badge_label} onChange={(v) => set("popular_badge_label", v)} /></Field>
        </SectionCard>

        <SectionCard id="edit-events" title="Eventos (home)" description={HOME_EDITOR_SECTIONS[4].description} preview={<EventsSectionPreview site={form} />}>
          <Field label="Etiqueta superior"><TextInput value={form.events_eyebrow} onChange={(v) => set("events_eyebrow", v)} /></Field>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Título — parte 1"><TextInput value={form.events_title_prefix} onChange={(v) => set("events_title_prefix", v)} /></Field>
            <Field label="Título — destacado"><TextInput value={form.events_title_highlight} onChange={(v) => set("events_title_highlight", v)} /></Field>
          </div>
          <Field label="Enlace agenda"><TextInput value={form.events_link_label} onChange={(v) => set("events_link_label", v)} /></Field>
        </SectionCard>

        <SectionCard id="edit-cta" title="Llamada a la acción" description={HOME_EDITOR_SECTIONS[5].description} preview={<CtaPreview site={form} />}>
          <div className="grid sm:grid-cols-3 gap-3">
            <Field label="Título — parte 1"><TextInput value={form.cta_title_prefix} onChange={(v) => set("cta_title_prefix", v)} /></Field>
            <Field label="Destacado"><TextInput value={form.cta_title_highlight} onChange={(v) => set("cta_title_highlight", v)} /></Field>
            <Field label="Sufijo"><TextInput value={form.cta_title_suffix} onChange={(v) => set("cta_title_suffix", v)} /></Field>
          </div>
          <Field label="Subtítulo"><TextInput value={form.cta_subtitle} onChange={(v) => set("cta_subtitle", v)} multiline /></Field>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Botón principal"><TextInput value={form.cta_btn_primary} onChange={(v) => set("cta_btn_primary", v)} /></Field>
            <Field label="Botón secundario"><TextInput value={form.cta_btn_secondary} onChange={(v) => set("cta_btn_secondary", v)} /></Field>
          </div>
        </SectionCard>

        <SectionCard id="edit-nav" title="Navegación" description={HOME_EDITOR_SECTIONS[6].description} preview={<NavPreview site={form} />}>
          <Field label="Botón unirse"><TextInput value={form.nav_join_label} onChange={(v) => set("nav_join_label", v)} /></Field>
          <Field label="Botón login usuario"><TextInput value={form.nav_login_label} onChange={(v) => set("nav_login_label", v)} /></Field>
        </SectionCard>

        <SectionCard id="edit-pages" title="Páginas internas" description={HOME_EDITOR_SECTIONS[7].description} preview={
          <div className="space-y-3">
            <PageHeaderPreview pageKey="contact_page" site={form} />
            <PageHeaderPreview pageKey="packages_page" site={form} />
          </div>
        }>
          {[
            ["contact_page", "Contacto"],
            ["packages_page", "Paquetes"],
            ["products_page", "Productos"],
            ["events_page", "Eventos"],
          ].map(([key, label]) => (
            <div key={key} className="glass p-4 rounded-sm space-y-2">
              <div className="font-display text-lg text-white">{label}</div>
              <Field label="Etiqueta"><TextInput value={form[key]?.eyebrow} onChange={(v) => setPage(key, "eyebrow", v)} /></Field>
              <div className="grid sm:grid-cols-2 gap-2">
                <Field label="Título — parte 1"><TextInput value={form[key]?.title_prefix} onChange={(v) => setPage(key, "title_prefix", v)} /></Field>
                <Field label="Destacado"><TextInput value={form[key]?.title_highlight} onChange={(v) => setPage(key, "title_highlight", v)} /></Field>
              </div>
            </div>
          ))}
        </SectionCard>
      </div>

      <div className="mt-8 flex justify-end">
        <button type="button" onClick={save} disabled={saving} className="btn-primary text-sm py-2">
          <Save size={14} /> {saving ? "Guardando…" : "Guardar sitio público"}
        </button>
      </div>
    </div>
  );
}
